import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button, Spacing, Stack } from "@noom/wax-component-library";

import {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { InformationalScreen } from "@/components/survey/questionTypes/core";
import {
  PregnantSelection,
  ProgramOffering,
  ScreenedOutReason,
} from "@/constants";
import { useAppContext } from "@/contexts";
import { useTrackEvent } from "@/hooks";
import { calculateBMIImperial } from "@/utils/bmi";
import { captureException } from "@/utils/sentry";

declare module "@/contexts" {
  interface SurveyAnswers {
    liabilityWaiverAlertEatingDisorder?: boolean;
    liabilityWaiverAlertBMI?: boolean;
    liabilityWaiverAlertIdealWeight?: boolean;
    liabilityWaiverAlertPregnant?: boolean;
  }
}

const QUESTION_ID_EATING_DISORDER = "liabilityWaiverAlertEatingDisorder";
const QUESTION_ID_BMI = "liabilityWaiverAlertBMI";
const QUESTION_ID_IDEAL_WEIGHT = "liabilityWaiverAlertIdealWeight";
const QUESTION_ID_PREGNANT = "liabilityWaiverAlertPregnant";

const QUESTION_ID_TO_REASON: Record<string, ScreenedOutReason> = {
  [QUESTION_ID_EATING_DISORDER]: ScreenedOutReason.EATING_DISORDER,
  [QUESTION_ID_BMI]: ScreenedOutReason.BMI_TOO_LOW,
  [QUESTION_ID_IDEAL_WEIGHT]: ScreenedOutReason.IDEAL_WEIGHT_BMI_TOO_LOW,
  [QUESTION_ID_PREGNANT]: ScreenedOutReason.PREGNANT,
} as const;

const getEligibilityTextKey = (
  questionId: string,
  eligiblePrograms: ProgramOffering[],
) => {
  const isEatingDisorderQuestion = questionId === QUESTION_ID_EATING_DISORDER;

  const prefix = `liabilityWaiverAlert.eligibilityText.${
    isEatingDisorderQuestion ? "eatingDisorder" : "default"
  }`;

  const offersDpp = eligiblePrograms.includes("DPP");
  const offersMed = eligiblePrograms.includes("MED");

  let suffix = "weight";

  if (offersDpp && offersMed) {
    suffix = "dpp_and_med";
  } else if (offersDpp) {
    suffix = "dpp";
  } else if (offersMed) {
    suffix = "med";
  }

  return `${prefix}.${suffix}`;
};

const LiabilityWaiverAlert: (
  questionId: string,
) => React.FC<QuestionComponentProps> = (questionId) => {
  const Component: React.FC<QuestionComponentProps> = ({
    onClickNext,
    surveyAnswers,
  }) => {
    const { eligibilityInfo, retriageEligibility, setSurveyAnswers } =
      useAppContext();
    const { trackSensitiveEvent } = useTrackEvent();
    useTranslation();

    const eligiblePrograms =
      retriageEligibility?.eligibleRetriagePrograms ||
      eligibilityInfo?.eligiblePrograms ||
      [];

    const eligibilityTextKey = getEligibilityTextKey(
      questionId,
      eligiblePrograms,
    );

    useEffect(() => {
      const reason = QUESTION_ID_TO_REASON[questionId];
      if (reason) {
        trackSensitiveEvent("b2bEnrollmentSensitiveUserExcluded", {
          exclusionReason: reason,
        });
      }
    }, [questionId]);

    useEffect(() => {
      // clear prior answer in case of back navigation.
      setSurveyAnswers({ ...surveyAnswers, [questionId]: undefined });
    }, []);

    return (
      <InformationalScreen
        heading={<Trans i18nKey="liabilityWaiverAlert.heading" />}
        questionId={questionId}
      >
        <Stack gap={Spacing[5]}>
          <Trans i18nKey="liabilityWaiverAlert.body" />
          <strong>
            <Trans i18nKey={eligibilityTextKey} />
          </strong>
          <Button
            colorScheme="primary"
            onClick={() => onClickNext(true)}
            size="xl"
            display="flex"
            margin="auto"
            maxWidth="400px"
          >
            <Trans i18nKey="liabilityWaiverAlert.cta" />
          </Button>
        </Stack>
      </InformationalScreen>
    );
  };

  return Component;
};

export const Q_LIABILITY_WAIVER_ALERT_EATING_DISORDER: QuestionDefinition = {
  id: QUESTION_ID_EATING_DISORDER,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({
    surveyAnswers: {
      eatingDisorder,
      liabilityWaiverAlertBMI,
      liabilityWaiverAlertIdealWeight,
      liabilityWaiverAlertPregnant,
    },
  }) =>
    eatingDisorder === "yes" &&
    !(
      liabilityWaiverAlertBMI ||
      liabilityWaiverAlertIdealWeight ||
      liabilityWaiverAlertPregnant
    ),
  component: LiabilityWaiverAlert(QUESTION_ID_EATING_DISORDER),
};

export const Q_LIABILITY_WAIVER_ALERT_BMI: QuestionDefinition = {
  id: QUESTION_ID_BMI,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({
    surveyAnswers: {
      heightWeight,
      liabilityWaiverAlertEatingDisorder,
      liabilityWaiverAlertIdealWeight,
      liabilityWaiverAlertPregnant,
    },
  }) => {
    if (!heightWeight) {
      captureException(
        Error(
          "Height and weight answers missing when determining is liabilityWaiverAlertBMI is shown",
        ),
      );
      return false;
    }
    const bmi = calculateBMIImperial(
      heightWeight.heightFeetPortion!,
      heightWeight.heightInchesPortion!,
      heightWeight.weight!,
    );

    if (
      bmi < 18.5 &&
      !(
        liabilityWaiverAlertEatingDisorder ||
        liabilityWaiverAlertIdealWeight ||
        liabilityWaiverAlertPregnant
      )
    ) {
      return true;
    }
    return false;
  },
  component: LiabilityWaiverAlert(QUESTION_ID_BMI),
};

export const Q_LIABILITY_WAIVER_ALERT_IDEAL_WEIGHT: QuestionDefinition = {
  id: QUESTION_ID_IDEAL_WEIGHT,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({
    surveyAnswers: {
      heightWeight,
      idealWeight,
      liabilityWaiverAlertEatingDisorder,
      liabilityWaiverAlertBMI,
      liabilityWaiverAlertPregnant,
    },
  }) => {
    if (
      !heightWeight ||
      heightWeight.heightFeetPortion === undefined ||
      heightWeight.heightInchesPortion === undefined ||
      idealWeight === undefined
    ) {
      captureException(
        Error(
          "Height and ideal weight answers missing when determining if liabilityWaiverAlertIdealWeight is shown",
        ),
      );
      return false;
    }
    const idealBMI = calculateBMIImperial(
      heightWeight.heightFeetPortion,
      heightWeight.heightInchesPortion,
      idealWeight,
    );

    if (
      idealBMI < 18.5 &&
      !(
        liabilityWaiverAlertEatingDisorder ||
        liabilityWaiverAlertBMI ||
        liabilityWaiverAlertPregnant
      )
    ) {
      return true;
    }
    return false;
  },
  component: LiabilityWaiverAlert(QUESTION_ID_IDEAL_WEIGHT),
};

export const Q_LIABILITY_WAIVER_ALERT_PREGNANT: QuestionDefinition = {
  id: QUESTION_ID_PREGNANT,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({
    surveyAnswers: {
      pregnant,
      liabilityWaiverAlertEatingDisorder,
      liabilityWaiverAlertBMI,
      liabilityWaiverAlertIdealWeight,
    },
  }) => {
    if (
      pregnant === PregnantSelection.YES &&
      !(
        liabilityWaiverAlertEatingDisorder ||
        liabilityWaiverAlertBMI ||
        liabilityWaiverAlertIdealWeight
      )
    ) {
      return true;
    }
    return false;
  },
  component: LiabilityWaiverAlert(QUESTION_ID_PREGNANT),
};
